import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router-dom'
import { getVariantConfig, isKeywordGraphApp } from './appVariant'
import { useBootstrapStore } from './stores/useBootstrapStore'
import { identifyMixpanelUser } from './Analytics'
import { posthogEnvironmentCall } from './analyticsEnvironment'

// Top-level path segments that are NOT graph pages. Anything else with
// at least two path segments (e.g. /:userName/:graphName[/*]) is treated
// as a graph page.
const NON_GRAPH_TOP_SEGMENTS = new Set([
    'apps',
    'import',
    'settings',
    'subscription',
    'upgrade',
    'api-access',
])

export default function HelmetCode() {
    const isKeywordGraph = isKeywordGraphApp()
    const { posthogKey, posthogHost, analyticsEnvironment } =
        getVariantConfig().trackers || {}
    const posthogApiHost = posthogHost || 'https://eu.i.posthog.com'
    const { pathname } = useLocation()

    // NEVER identify/alias the server-side ab_landing uuid here — exposure
    // persons must stay separate or the landing experiment breaks.
    const authenticatedUsername = useBootstrapStore(state =>
        state.user?.isAuthenticated ? state.user.username : null
    )
    useEffect(() => {
        if (!authenticatedUsername) return
        identifyMixpanelUser(authenticatedUsername)
        // posthog can load after this effect (bare templates get it only via
        // the Helmet-injected snippet below), and identify() does not exist as
        // a function until the snippet's init() — queue the call on the
        // posthog array instead; array.js replays it on load.
        const { email, plan, status } = useBootstrapStore.getState().user || {}
        const personProperties = { email, plan, status }
        if (typeof window.posthog?.identify === 'function') {
            window.posthog.identify(authenticatedUsername, personProperties)
        } else {
            ;(window.posthog = window.posthog || []).push([
                'identify',
                authenticatedUsername,
                personProperties,
            ])
        }
    }, [authenticatedUsername])

    const isGraphPage = useMemo(() => {
        const segments = pathname.split('/').filter(Boolean)
        if (segments.length < 2) return false
        return !NON_GRAPH_TOP_SEGMENTS.has(segments[0])
    }, [pathname])

    // Third-party tracking config. KeywordGraph variant ships a slimmer
    // stack (its own GTM container, no Hotjar / FirstPromoter); the main
    // InfraNodus variant gets the full set.
    const trackers = useMemo(
        () =>
            isKeywordGraph
                ? {
                      gtmId: 'GTM-P3446KG',
                      gtagId: 'G-P85D7051QY',
                      hotjar: null,
                      firstPromoter: null,
                  }
                : {
                      gtmId: 'GTM-NTJCSNB',
                      gtagId: 'G-V8SCV8RZT6',
                      hotjar: { id: 956189, sv: 6 },
                      firstPromoter: { cid: 'ocphapw0' },
                  },
        [isKeywordGraph]
    )

    useEffect(() => {
        // The N8N chat widget is only loaded on non-graph pages (landing,
        // apps, settings, subscription, etc.) to avoid interfering with
        // the graph editing UI.
        if (isGraphPage) return undefined

        const script = document.createElement('script')
        script.src = 'https://n8n-chat-widget.com/widget.min.js'
        script.async = true

        script.onload = () => {
            if (typeof window.N8NWidget !== 'undefined') {
                new window.N8NWidget({
                    apiKey: 'n8n_live_f8d1a57a2490470f9399485b2f6c18184be9dd6f',
                    widgetId: '28c5a59a-394f-4dfd-90b6-cc5591a11b31',
                })
            }
        }

        document.head.appendChild(script)

        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script)
            }
        }
    }, [isGraphPage])

    return (
        <>
            <Helmet>
                <link
                    rel="icon"
                    href="/images/favicon.svg"
                    type="image/svg+xml"
                />
                <script>{`
                window.dataLayer = window.dataLayer || [];
                `}</script>

                {trackers.gtmId && (
                    <script>{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${trackers.gtmId}');`}</script>
                )}

                {trackers.gtagId && (
                    <script
                        async
                        src={`https://www.googletagmanager.com/gtag/js?id=${trackers.gtagId}`}
                    ></script>
                )}

                {trackers.gtagId && (
                    <script>{`
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());

                    gtag('config', '${trackers.gtagId}');
                `}</script>
                )}

                {trackers.hotjar && (
                    <script>{`
                    (function(h,o,t,j,a,r){
                        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                        h._hjSettings={hjid:${trackers.hotjar.id},hjsv:${trackers.hotjar.sv}};
                        a=o.getElementsByTagName('head')[0];
                        r=o.createElement('script');r.async=1;
                        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                        a.appendChild(r);
                    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
                `}</script>
                )}

                {trackers.firstPromoter && (
                    <script>{`(function(w){w.fpr=w.fpr||function(){w.fpr.q = w.fpr.q||[];w.fpr.q[arguments[0]=='set'?'unshift':'push'](arguments);};})(window);
                    fpr("init", {cid:"${trackers.firstPromoter.cid}"});
                    fpr("click");
                    `}</script>
                )}
                {trackers.firstPromoter && (
                    <script
                        src="https://cdn.firstpromoter.com/fpr.js"
                        async
                    ></script>
                )}
                <script>{`!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
                posthog.init('${posthogKey}',{api_host:'${posthogApiHost}', person_profiles: 'identified_only'});
                ${posthogEnvironmentCall(analyticsEnvironment)}
                setTimeout(function(){try{posthog.set_config({session_recording: {recordCanvas: true, canvasFps: 4, canvasQuality: '0.6'}});posthog.stopSessionRecording();posthog.startSessionRecording();}catch(e){}}, 2500);`}</script>
            </Helmet>
        </>
    )
}
